import { logger } from "@-/dev.improve.haniverse.lib.logger";
import {
  getDeviceAccount,
  getDeviceSetting,
  deviceSettingsStorage,
  type DeviceAccount,
} from "@-/dev.improve.haniverse.lib.device-settings";
import {
  getActiveActorTursoSync,
  type ActorDatabaseConnection,
} from "@-/dev.improve.haniverse.database.actor";
import { addDatabaseChangeListener, emitManualDatabaseChange } from "@-/com.npmjs.expo-sqlite";
import { AppState } from "@-/com.npmjs.react-native";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { getTableName, is } from "@-/com.npmjs.drizzle-orm";
import { SQLiteTable } from "@-/com.npmjs.drizzle-orm/sqlite-core";
import ensureError from "@-/com.npmjs.ensure-error";
import {
  classifyActorSyncFailure,
  clearSyncFailure,
  createActorSyncSupervisor,
  describeActorSyncFailure,
  markInitialPullComplete,
  markInitialPullStarted,
  setSyncFailure,
  setSyncPhase,
  setSyncedEntryCount,
} from "@-/dev.improve.haniverse.lib.sync-client";
import useLatest from "@-/com.npmjs.use-latest";
import onetime from "@-/com.npmjs.onetime";
import delay from "@-/com.npmjs.delay";
import {
  createBoundedSerialQueue,
  isDeadlineExceededError,
  withDeadline,
} from "@-/dev.improve.haniverse.lib.deadline";
import { drainActorTursoPull } from "#drain-actor-turso-pull";
import { runWithProgressDeadline } from "#run-with-progress-deadline";
import { safeSetTimeout } from "@-/dev.improve.haniverse.lib.scheduler";
import { PERF_OP, startPerfSpan } from "@-/dev.improve.haniverse.lib.sentry/perf";
import { useEffect, useState } from "react";
import { connectActorAutoSync, notifyActorSync } from "#connect-actor-auto-sync";
import {
  acknowledgeSyncPush,
  ensureSyncPushRegistration,
  watchSyncPushToken,
} from "@-/dev.improve.haniverse.lib.sync-push";
import {
  completeRemoteCatchUp,
  getRemoteChangeSignalSeq,
  markOtherDeviceChangesPulled,
  markRemoteCatchUpFailed,
} from "@-/dev.improve.haniverse.lib.sync-push.signal";
import { createBackgroundSyncTask } from "#create-background-sync-task";
import {
  type BackupOperation,
  withActorSyncBackupCoordination,
} from "@-/dev.improve.haniverse.lib.actor-backups";

// Changes applied by the native sync engine's `pull()` never fire the expo-sqlite
// update hook nor the adapter's manual emitter. Re-broadcast a synthetic change
// per actor table after a pull so the standard invalidation chain refetches.
const REMOTE_SYNC_PULL_DATABASE_NAME = "actor-remote-sync-pull";
const PULL_RETRY_ATTEMPTS = 5;
const PULL_RETRY_DELAY_MS = 1000;
// Bound the wait for the lazily-opened Turso sync handle. If it never appears (a connection open
// that silently never set `activeActorTursoSync`), stop polling after this budget and SETTLE the
// sync as failed — so the "actor-syncing" indicator clears and the app stops implying it's still
// making progress, instead of polling every 300ms forever.
//
// The budget MUST outlast the slowest LEGITIMATE open, or this poller races the open it is waiting
// for and settles `handle-never-opened` on a connection that then opens seconds later — leaving
// no-op pull/push installed permanently (the settle is terminal: it stops polling and never
// re-arms). `open-actor-turso-sync-connection.ts` bounds `connect()` at CONNECT_TIMEOUT_MS=20s and
// retries CONNECT_MAX_ATTEMPTS=3 times, so a worst-case open is ~60s of connect alone, before the
// per-attempt bootstrap reset and a brand-new account's remote provisioning. The old 30s budget sat
// UNDER that floor, so a cold sign-in on a slow device (every Android CI emulator, run 35630538535:
// 9 flows wedged at `phase=handle-never-opened` across all 5 shards) settled as failed while the
// open was still in flight. 300ms × 400 = 120s: double the 60s connect ceiling, so only a genuinely
// dead open reaches it.
const MAX_HANDLE_POLL_ATTEMPTS = 400;
const HANDLE_POLL_INTERVAL_MS = 300;
// A signed-in device whose actor connection never appears at all (the open never settles) held the
// "Loading your learning data" gate forever: the handle poll above only starts once a connection
// exists. Give the connection the same budget as the handle, then settle as a visible failure.
const ACTOR_CONNECTION_WAIT_DEADLINE_MS = MAX_HANDLE_POLL_ATTEMPTS * HANDLE_POLL_INTERVAL_MS;
// Auto-push (send half of auto-sync) coalesces a burst of local writes into one
// push. A single user action (e.g. reviewing a card) fires several mutating
// statements across tables — each emits its own change event — so a short
// coalescing window collapses them into one `push()` + one `sync.notify()` nudge.
const AUTO_PUSH_DELAY_MS = 1500;
// Sync push registration waits until launch work has settled; it is never on the path to a screen.
const SYNC_PUSH_REGISTRATION_DELAY_MS = 5000;
// A pull that applies no batch for this long has stalled. It outlasts one bounded `pull()` call
// (30s, `PULL_CALL_TIMEOUT_MS`) plus its retry backoff, so one slow request is retried, but a
// connection that never answers ends in a visible, retryable `pull` failure within a minute
// instead of the former fixed 180s — which, stacked on provisioning and the handle wait, kept the
// "Syncing data from cloud" placeholder up for minutes. Every applied batch restarts it, so a large
// first sync that is moving keeps going.
const PULL_IDLE_DEADLINE_MS = 60_000;
// Even a pull that keeps applying batches ends: `MAX_PULL_BATCHES` (64) batches of real transfer
// finish far sooner; only a drain that never reaches the remote revision gets here.
const PULL_CEILING_MS = 10 * 60_000;
const PULL_OPERATION = "Actor Turso pull";
// One `push()` uploads this connection's pending CDC in a single request. It must finish inside the
// backup/sync coordination's idle deadline (60s, `BACKUP_OPERATION_IDLE_DEADLINE_MS`), which is
// what keeps a push that never answers from holding sync and the sign-in swap.
const PUSH_CALL_DEADLINE_MS = 45_000;
// Operations wait in line behind each other; the ones ahead are bounded (idle deadlines above and
// the coordination's), so this only has to outlast a long pull that keeps making progress.
const SYNC_QUEUE_WAIT_DEADLINE_MS = PULL_CEILING_MS;
// Counting replica rows is observational progress for the placeholder; a local aggregate query.
const ENTRY_COUNT_DEADLINE_MS = 10_000;
const getActorTableNames = onetime(() =>
  Object.values(actorSchema).flatMap((value) =>
    is(value, SQLiteTable) ? [getTableName(value)] : [],
  ),
);
const getActorTableNameSet = onetime(() => new Set<string>(getActorTableNames()));
const getActorEntryCountSql = onetime(
  () =>
    `SELECT ${getActorTableNames()
      .map((name) => `(SELECT COUNT(*) FROM "${name.replaceAll('"', '""')}")`)
      .join(" + ")} AS count`,
);

export async function countActorReplicaEntries(db: ActorDatabaseConnection): Promise<number> {
  const row = await db.getFirstAsync<{ count: number }>(getActorEntryCountSql());
  // A `SELECT (…) AS count` always yields exactly one row. Reporting 0 for a missing one would
  // disguise a broken connection as an empty replica.
  if (row === null) {
    throw new Error("Actor replica entry count query returned no row");
  }
  return row.count;
}

/** The shared database emitter also carries device/content writes and remote pull echoes. */
export function isLocalActorDatabaseChange(event: {
  databaseName: string;
  tableName: string;
}): boolean {
  return (
    event.databaseName !== REMOTE_SYNC_PULL_DATABASE_NAME &&
    getActorTableNameSet().has(event.tableName)
  );
}

type ActorTursoSync = NonNullable<ReturnType<typeof getActiveActorTursoSync>>;

/**
 * Whether a failed pull is worth another attempt right away. A stalled or dropped request is, and
 * so is a download that could not replay an unsent change: uploading that change first removes
 * what the replay tripped over. Everything else has its own response (see
 * `actor-sync-supervisor.ts`) and is left to the next cycle.
 */
function isImmediatelyRetryablePull(error: unknown): boolean {
  const failureClass = classifyActorSyncFailure(error);
  return failureClass === "transient" || failureClass === "replay-failed";
}

function emitActorTablesChangedByRemotePull(): void {
  for (const tableName of getActorTableNames()) {
    emitManualDatabaseChange({
      databaseFilePath: "",
      databaseName: REMOTE_SYNC_PULL_DATABASE_NAME,
      rowId: 0,
      tableName,
    });
  }
}

/**
 * What sync does by itself when a failure calls for more than another try (see
 * `actor-sync-supervisor.ts`). Passed in, like `userId`, so this hook stays free of the query
 * client and the auth client.
 */
export interface ActorSyncRecovery {
  /**
   * Replace the replica: carry its unsent changes to the cloud through the import, then download
   * it fresh. Also finishes a rebuild that a crash cut short (`rebuildPending` on the account).
   */
  rebuild(): Promise<void>;
  /** Ask the server for a new device credential and reopen with it; `false` when signed out. */
  refreshCredential(): Promise<boolean>;
  /** Replace the sync engine with a fresh one on the same replica. */
  reopen(): Promise<void>;
}

export type ActorRemoteSync = {
  isInitialPullComplete: boolean;
  /** Verified persisted data is usable while the remote revision check runs. */
  isLocalReplicaReady?: boolean;
  pullFromRemote: () => Promise<boolean>;
  pushToRemote: () => Promise<void>;
};

/**
 * Observe the WHOLE pull, including queue admission and backup recovery checks.
 * Those checks can fail before the drain starts. The caller's wait restarts on
 * every applied batch (`progress`) and ends after `PULL_IDLE_DEADLINE_MS` of
 * silence; the queues underneath are bounded on their own, so an abandoned pull
 * no longer holds them. A late successful drain can still clear the timeout
 * failure.
 */
export async function runActorTursoPullWithStatus(
  pull: (progress: () => void) => Promise<boolean>,
  isDisposed: () => boolean,
  onSettled: () => void,
  /** What the user is told about a failure. Defaults to the error's own message. */
  describeFailure: (error: unknown) => string = (error) => ensureError(error).message,
): Promise<boolean> {
  let progress = () => {};
  const work = Promise.resolve()
    .then(() => pull(() => progress()))
    .then((hadChanges) => {
      if (isDisposed()) {
        throw new Error("Actor Turso sync is disconnected.");
      }
      clearSyncFailure("pull");
      markInitialPullComplete();
      setSyncPhase("pulled");
      onSettled();
      return hadChanges;
    });
  try {
    // Never advertise an unbounded wait on backup coordination, a stuck local query
    // or a silent connection as loading.
    return await runWithProgressDeadline(
      {
        ceilingMs: PULL_CEILING_MS,
        idleDeadlineMs: PULL_IDLE_DEADLINE_MS,
        operation: PULL_OPERATION,
      },
      (onProgress) => {
        progress = onProgress;
        return work;
      },
    ).catch((error: unknown) => {
      // Only the pull's own deadlines mean the cloud did not answer in time. A nested one (a local
      // read before the pull, such as the legacy recovery hold) keeps its message, which names
      // what did not answer.
      throw isDeadlineExceededError(error) && error.operation.startsWith(PULL_OPERATION)
        ? new Error("Cloud sync timed out. Sync may still be finishing on this device.", {
            cause: error,
          })
        : error;
    });
  } catch (error) {
    if (!isDisposed()) {
      setSyncFailure({ at: Date.now(), kind: "pull", message: describeFailure(error) });
      setSyncPhase("pull-failed");
      // A failure ends preparation, but must never advertise a successful drain.
      onSettled();
    }
    throw error;
  }
}

/**
 * Exposes Turso replication controls for the actor DB. The actor
 * connection IS the Turso sync `Database`, so `pull()` / `push()` operate on the
 * same connection the app writes through.
 *
 * Auto-sync enables both a
 * Pusher subscription that triggers `pullFromRemote()` when another of the
 * user's devices publishes an actor-DB change, and a change-listener-driven
 * auto-push that replicates local writes and notifies those other devices.
 *
 * `userId` (the signed-in Better Auth user id) scopes the per-user Pusher
 * channel (`actor-<userId>`); it is passed in by the caller rather than read via
 * `useSession()` here so this low-level hook stays free of the auth client.
 */
export function useActorTursoRemoteSync(
  db: ActorDatabaseConnection | null,
  deviceAccount: DeviceAccount | null,
  userId: string | null,
  recovery: ActorSyncRecovery,
): ActorRemoteSync | null {
  const [actorRemoteSync, setActorRemoteSync] = useState<ActorRemoteSync | null>(null);
  // Read at the moment a response is needed, so a new object never restarts the sync loop.
  const recoveryRef = useLatest(recovery);

  useEffect(() => {
    logger(import.meta.location).info("remote-sync:effect", {
      autoSync: true,
      hasDb: db != null,
      hasDeviceAccount: deviceAccount != null,
      syncHandleActive: getActiveActorTursoSync() != null,
    });
    if (deviceAccount == null || db == null) {
      setSyncPhase(deviceAccount == null ? "no-account" : "no-db");
      setSyncedEntryCount(null);
      setActorRemoteSync(null);
      if (deviceAccount == null) {
        return;
      }
      // Bounded: a new connection re-runs this effect (clearing the timer); one that never arrives
      // ends preparation with a recorded failure instead of an endless placeholder.
      const connectionTimer = safeSetTimeout(
        () => {
          setSyncPhase("db-never-opened");
          logger(import.meta.location).error(
            "Actor database connection never opened; settling sync as failed",
          );
          setSyncFailure({
            at: Date.now(),
            kind: "pull",
            message: "Your learning data did not finish opening. Restart Han and try again.",
          });
          const unavailable = async (): Promise<never> => {
            throw new Error("Cloud sync is unavailable until your learning data opens.");
          };
          setActorRemoteSync({
            isInitialPullComplete: true,
            pullFromRemote: unavailable,
            pushToRemote: unavailable,
          });
        },
        ACTOR_CONNECTION_WAIT_DEADLINE_MS,
        "actor-turso-connection-wait",
      );
      return () => {
        clearTimeout(connectionTimer);
        setActorRemoteSync(null);
      };
    }

    let disposed = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let actorConnectionOpenStarted = false;
    let actorConnectionOpenFailed = false;
    let syncStarted = false;
    let handlePollAttempts = 0;
    let disconnectAutoSync: (() => void) | undefined;
    let cancelBackgroundPull: (() => void) | undefined;
    let appStateListener: { remove(): void } | undefined;
    let catchUpTimer: ReturnType<typeof setInterval> | undefined;
    let unwatchRecoveryHold: (() => void) | undefined;
    let stopSyncPushRegistration: (() => void) | undefined;
    // Auto-push (send half of auto-sync) teardown handles: the change-listener
    // subscription and the pending background task, both cleared on effect cleanup.
    let autoPushChangeListener: { remove(): void } | undefined;
    let cancelBackgroundPush: (() => void) | undefined;

    // Pull/push on this connection run one at a time. Each operation is bounded underneath (its
    // backup/sync coordination expires without progress), so the line advances past one that
    // never answers instead of parking every later pull behind it; the connection's own lock keeps
    // a straggling native call from interleaving with the next one.
    const syncQueue = createBoundedSerialQueue({
      name: "actor Turso sync",
      onOverrun: "advance",
      runDeadline: PULL_CEILING_MS,
      waitDeadline: SYNC_QUEUE_WAIT_DEADLINE_MS,
    });
    // Reset before handle polling too, so an account swap never displays the previous count.
    setSyncedEntryCount(null);
    const publishEntryCount = async () => {
      try {
        const count = await withDeadline(
          { deadline: ENTRY_COUNT_DEADLINE_MS, operation: "count actor replica entries" },
          () => countActorReplicaEntries(db),
        );
        if (!disposed) {
          setSyncedEntryCount(count);
        }
      } catch (error) {
        // Progress is observational: a counting failure must never fail or retry replication.
        if (!disposed) {
          setSyncedEntryCount(null);
          logger(import.meta.location).warn("Actor replica entry count failed", error);
        }
      }
    };
    const runExclusive = <T>(
      label: string,
      op: (coordination: BackupOperation) => Promise<T>,
      options?: { logFailure?: boolean },
    ): Promise<T> =>
      syncQueue.run(label, async () => {
        if (disposed) {
          throw new Error("Actor Turso sync is disconnected.");
        }
        try {
          return await withActorSyncBackupCoordination(db, (coordination) => {
            // Backup coordination can queue this operation across an account/connection swap.
            if (disposed) {
              throw new Error("Actor Turso sync is disconnected.");
            }
            return op(coordination);
          });
        } catch (error: unknown) {
          if (options?.logFailure !== false) {
            logger(import.meta.location).error(`Actor Turso ${label} failed`, error);
          }
          throw error;
        }
      });

    const start = (sync: ActorTursoSync) => {
      logger(import.meta.location).info("remote-sync:start", {
        autoSync: true,
        syncLive: true,
      });
      // Every failure of this connection is classified here, and each class has its automatic
      // response: retry, a new credential, a fresh engine, or a rest until the app is updated.
      const supervisor = createActorSyncSupervisor({
        onError: (message, error) => {
          logger(import.meta.location).warn(message, error);
        },
        // One line per automatic response, so the device's logs (Sentry Logs) show what sync did
        // about a failure, not only that one happened.
        onTransition: (transition) => {
          logger(import.meta.location).warn("remote-sync:supervisor", transition);
        },
        rebuild: () => recoveryRef.current.rebuild(),
        refreshCredential: () => recoveryRef.current.refreshCredential(),
        reopen: () => recoveryRef.current.reopen(),
      });
      // Read from the store, not from the `deviceAccount` prop: the hook behind that prop only
      // re-renders when the credential changes, so the prop still shows a marker the rebuild has
      // cleared, and acting on it started the rebuild again after every reopen.
      const rebuildPending = getDeviceAccount()?.rebuildPending;
      if (rebuildPending !== undefined) {
        // A rebuild was cut short (a crash, or the import could not be sent). The loop below
        // starts regardless; the rebuild replaces the connection when it gets through.
        logger(import.meta.location).warn("remote-sync:rebuild-pending", {
          importId: rebuildPending,
        });
        void recoveryRef.current.rebuild().catch((error: unknown) => {
          logger(import.meta.location).warn("Finishing the pending replica rebuild failed", error);
        });
      }
      const describeFailure = (error: unknown): string => {
        const failureClass = supervisor.failed(error);
        logger(import.meta.location).warn("Actor Turso sync failure classified", {
          failureClass,
          message: ensureError(error).message,
        });
        return describeActorSyncFailure(failureClass);
      };
      // The engine reports this device's own upload as pulled changes on the next pull, so only a
      // pull with no upload since the previous one can be said to bring another device's study.
      let uploadedSincePull = false;
      // Upload before every download: the download then only has to replay writes that raced it,
      // and a change the cloud copy cannot take is set aside there instead of failing the replay.
      // Best-effort, because a download can still succeed when an upload cannot.
      const uploadBeforePull = async (): Promise<void> => {
        try {
          const { cdcOperations } = await sync.stats();
          if (cdcOperations > 0) {
            await withDeadline(
              { deadline: PUSH_CALL_DEADLINE_MS, operation: "Actor Turso push() before pull" },
              () => sync.push(),
            );
            uploadedSincePull = true;
          }
        } catch (error) {
          logger(import.meta.location).warn("Actor Turso upload before pull failed", error);
        }
      };
      const recoveryHeld = getDeviceSetting("isBackupRecoveryHoldEnabled");
      if (!recoveryHeld) {
        markInitialPullStarted();
      }
      setSyncPhase(recoveryHeld ? "recovery-held" : "pulling");

      const pullFromRemote = () => {
        // The sync push signal this pull answers for (see `remote-change-signal.ts`): read when the
        // download starts, so a push that lands during it keeps the device behind for the next pull.
        const catchUp = { mayBeOwnUpload: true, signalSeq: getRemoteChangeSignalSeq() };
        return runActorTursoPullWithStatus(
          (progress) =>
            runExclusive(
              "manual pull",
              async (coordination) => {
                catchUp.signalSeq = getRemoteChangeSignalSeq();
                // Admission and every applied batch are progress for both the caller's wait and
                // the coordination's turn; a stalled drain lets both expire.
                const reportProgress = () => {
                  progress();
                  coordination.report({ completed: 0, label: "Loading from cloud", total: 1 });
                };
                reportProgress();
                for (let attempt = 1; !disposed && attempt <= PULL_RETRY_ATTEMPTS; attempt++) {
                  try {
                    await uploadBeforePull();
                    reportProgress();
                    const hadChanges = await drainActorTursoPull(
                      sync,
                      emitActorTablesChangedByRemotePull,
                      () => disposed,
                      async () => {
                        reportProgress();
                      },
                    );
                    catchUp.mayBeOwnUpload = uploadedSincePull;
                    uploadedSincePull = false;
                    // Do not scan every actor table before every revision check or batch.
                    // An unchanged replica does not change progress counts.
                    if (hadChanges) {
                      await publishEntryCount();
                    }
                    if (disposed) {
                      throw new Error("Actor Turso sync is disconnected.");
                    }
                    return hadChanges;
                  } catch (error) {
                    if (disposed) {
                      throw error;
                    }
                    const ensuredError = ensureError(error);
                    const canRetry =
                      attempt < PULL_RETRY_ATTEMPTS && isImmediatelyRetryablePull(ensuredError);
                    logger(import.meta.location).warn("Actor Turso pull failed", {
                      attempt,
                      maxAttempts: PULL_RETRY_ATTEMPTS,
                      message: ensuredError.message,
                      retrying: canRetry,
                    });
                    if (!canRetry) {
                      throw error;
                    }
                    await delay(PULL_RETRY_DELAY_MS * attempt);
                    coordination.check();
                  }
                }
                throw new Error("Actor Turso pull stopped before completing.");
              },
              { logFailure: false },
            ),
          () => disposed,
          () =>
            setActorRemoteSync((current) =>
              current == null || current.isInitialPullComplete
                ? current
                : { ...current, isInitialPullComplete: true },
            ),
          describeFailure,
        ).then(
          (hadChanges) => {
            supervisor.succeeded();
            if (hadChanges && !catchUp.mayBeOwnUpload) {
              // Another device studied: the Learn deck re-resolves its upcoming cards.
              markOtherDeviceChangesPulled();
            }
            // Caught up on a sync push: tell the server, so the next upload elsewhere pushes this
            // device again (it pushes each device once per stretch of being behind).
            if (completeRemoteCatchUp(catchUp.signalSeq)) {
              void acknowledgeSyncPush().catch((error: unknown) => {
                logger(import.meta.location).warn("Acknowledging the sync push failed", error);
              });
            }
            return hadChanges;
          },
          (error: unknown) => {
            // Learn's Start stops saying "Syncing from cloud" for a catch-up that is not happening.
            markRemoteCatchUpFailed(catchUp.signalSeq);
            throw error;
          },
        );
      };

      const pushToRemote = () =>
        runExclusive("manual push", async (coordination) => {
          try {
            coordination.report({ completed: 0, label: "Saving to cloud", total: 1 });
            await withDeadline(
              { deadline: PUSH_CALL_DEADLINE_MS, operation: "Actor Turso push()" },
              () => sync.push(),
            );
            uploadedSincePull = true;
            if (!disposed) {
              supervisor.succeeded();
              clearSyncFailure("push");
            }
          } catch (error) {
            if (!disposed) {
              setSyncFailure({ at: Date.now(), kind: "push", message: describeFailure(error) });
            }
            throw error;
          }
        });

      setActorRemoteSync({
        isInitialPullComplete: recoveryHeld,
        isLocalReplicaReady: sync.hasLocalReplica === true,
        pullFromRemote,
        pushToRemote,
      });

      const backgroundPull = createBackgroundSyncTask({
        onError: (error) => {
          if (!disposed) {
            logger(import.meta.location).warn("Actor background sync failed", error);
          }
        },
        run: async () => {
          if (
            !getDeviceSetting("isBackupRecoveryHoldEnabled") &&
            supervisor.canRunAutomatically()
          ) {
            await pullFromRemote();
          }
        },
      });
      cancelBackgroundPull = backgroundPull.cancel;
      // Returning devices render their local replica first. Fresh sign-in still waits for its
      // initial drain. This scheduling complements the engine's network/local lock separation.
      if (recoveryHeld) {
        // The explicit recovery action resumes this same handle. Recreating it
        // on a setting change would race the action with effect teardown.
      } else if (sync.hasLocalReplica === true) {
        backgroundPull.request();
      } else {
        void startPerfSpan(
          "actor remote-sync initial pull",
          { attributes: { "han.db.stage": "initial-pull" }, op: PERF_OP.db },
          () => pullFromRemote(),
        ).catch((error: unknown) => {
          if (!disposed) {
            logger(import.meta.location).error("Actor Turso initial pull failed", error);
          }
        });
      }

      // Auto-sync needs a signed-in user id to scope the per-user Pusher channel.
      // Signed-out local-only accounts never publish to another account's channel.
      if (userId != null) {
        const onAutoSyncError = (error: unknown) => {
          if (disposed) {
            return;
          }
          const ensuredError = ensureError(error);
          // Realtime hints are optional: foreground/timer catch-up still runs.
          // Actual pull failures are recorded by pullFromRemote itself.
          logger(import.meta.location).warn("Actor realtime sync hints failed", ensuredError);
        };

        // Receive half: pull when another of the user's devices publishes a change.
        disconnectAutoSync = connectActorAutoSync({
          loadFromCloud: async () => backgroundPull.request(),
          onError: onAutoSyncError,
          userId,
        });

        // Send half: push local writes automatically, then nudge the user's other
        // devices to pull via the `sync.notify` oRPC procedure. The change listener
        // fires per mutating statement through the Turso sync connection (the only
        // writes that CDC captures + pushes); the background task coalesces a burst.
        // Start dirty to flush durable CDC left by an offline session/process exit. Only a
        // successful upload AND notification acknowledge the captured write generation.
        let localWriteGeneration = 1;
        let pushedGeneration = 0;
        const backgroundPush = createBackgroundSyncTask({
          delayMs: AUTO_PUSH_DELAY_MS,
          onError: (error) => {
            if (!disposed) {
              // `pushToRemote` has classified and recorded its own failures.
              logger(import.meta.location).warn("Actor background upload failed", error);
            }
          },
          run: async () => {
            if (
              disposed ||
              localWriteGeneration === pushedGeneration ||
              getDeviceSetting("isBackupRecoveryHoldEnabled") ||
              !supervisor.canRunAutomatically()
            ) {
              return;
            }
            const generation = localWriteGeneration;
            await pushToRemote();
            if (disposed) {
              return;
            }
            try {
              await notifyActorSync();
            } catch (error) {
              // The upload succeeded; only the nudge to the user's other devices failed, and they
              // still catch up on resume and on the fallback timer. Recording it as an upload
              // failure made the banner claim the changes were on this device only. Leave the
              // generation unacknowledged so the next run retries the nudge.
              logger(import.meta.location).warn("Actor sync notify failed after upload", error);
              return;
            }
            pushedGeneration = generation;
          },
        });
        cancelBackgroundPush = backgroundPush.cancel;
        backgroundPush.request();

        // Realtime hints can be lost while suspended or disconnected. Keep a cheap cursor
        // check on resume and an active-only fallback for deployments without Pusher.
        const catchUp = () => {
          if (getDeviceSetting("isBackupRecoveryHoldEnabled")) {
            return;
          }
          backgroundPull.request();
          if (localWriteGeneration !== pushedGeneration) {
            backgroundPush.request();
          }
        };
        unwatchRecoveryHold = deviceSettingsStorage.watch((_event, key) => {
          if (key === "device:isBackupRecoveryHoldEnabled") {
            catchUp();
          }
          // A sync push arrived (the background task bumps the signal). Pull now if the app is in
          // front; a push that woke it in the background waits for the next foreground catch-up.
          if (key === "device:remoteChangeSignalSeq" && AppState.currentState === "active") {
            catchUp();
          }
        });
        appStateListener = AppState.addEventListener("change", (state) => {
          if (state === "active") {
            catchUp();
          }
        });
        catchUpTimer = setInterval(() => {
          if (AppState.currentState === "active") {
            catchUp();
          }
        }, 60_000);

        // Let the server wake this install with a silent push when another device uploads. Off the
        // launch path: it needs the native push token and, when something changed, two requests.
        const registrationTimer = safeSetTimeout(
          () => {
            void ensureSyncPushRegistration(userId).catch((error: unknown) => {
              if (!disposed) {
                logger(import.meta.location).warn("Sync push registration failed", error);
              }
            });
          },
          SYNC_PUSH_REGISTRATION_DELAY_MS,
          "sync-push-registration",
        );
        const unwatchSyncPushToken = watchSyncPushToken(userId);
        stopSyncPushRegistration = () => {
          clearTimeout(registrationTimer);
          unwatchSyncPushToken();
        };

        autoPushChangeListener = addDatabaseChangeListener((event) => {
          if (disposed) {
            return;
          }
          // Ignore the synthetic re-broadcast emitted AFTER a remote pull applies
          // changes (see `emitActorTablesChangedByRemotePull`) — those rows came
          // FROM the remote, so pushing/notifying on them would loop devices.
          if (!isLocalActorDatabaseChange(event)) {
            return;
          }
          localWriteGeneration += 1;
          backgroundPush.request();
        });
      }
    };

    const waitForHandle = () => {
      if (disposed || actorConnectionOpenFailed || syncStarted) {
        return;
      }
      if (!actorConnectionOpenStarted) {
        actorConnectionOpenStarted = true;
        // Force lazy connections to open so `getActiveActorTursoSync()` can be set.
        if (typeof db.execAsync === "function") {
          void db
            .execAsync("PRAGMA user_version;")
            .then(() => {
              // The open itself signals readiness. Do not add up to 300ms of polling
              // latency before the local replica becomes usable by the UI.
              if (pollTimer !== undefined) {
                clearTimeout(pollTimer);
              }
              waitForHandle();
            })
            .catch((error: unknown) => {
              if (disposed) {
                return;
              }
              actorConnectionOpenFailed = true;
              if (pollTimer !== undefined) {
                clearTimeout(pollTimer);
              }
              const ensuredError = ensureError(error);
              setSyncFailure({ at: Date.now(), kind: "pull", message: ensuredError.message });
              setActorRemoteSync({
                isInitialPullComplete: true,
                pullFromRemote: async () => {
                  throw ensuredError;
                },
                pushToRemote: async () => {
                  throw ensuredError;
                },
              });
            });
        }
      }
      const sync = getActiveActorTursoSync();
      if (sync != null) {
        syncStarted = true;
        if (pollTimer !== undefined) {
          clearTimeout(pollTimer);
        }
        start(sync);
        return;
      }
      // BOUND the wait: if the lazily-opened sync handle never appears (a connection open that
      // silently never set `activeActorTursoSync`), stop after `MAX_HANDLE_POLL_ATTEMPTS` and settle
      // the sync as failed instead of polling every 300ms forever — otherwise the "actor sync
      // preparing" gate never clears and the indicator implies it's still making progress. The
      // pull/push fail visibly until a connection opens, so a manual retry cannot
      // advertise a successful transfer through an unavailable handle.
      handlePollAttempts += 1;
      setSyncPhase("awaiting-handle");
      if (handlePollAttempts >= MAX_HANDLE_POLL_ATTEMPTS) {
        setSyncPhase("handle-never-opened");
        logger(import.meta.location).error(
          "Actor Turso sync handle never appeared; settling sync as failed",
          { attempts: handlePollAttempts },
        );
        setSyncFailure({
          at: Date.now(),
          kind: "pull",
          message: "Actor Turso sync handle did not open.",
        });
        setActorRemoteSync({
          isInitialPullComplete: true,
          pullFromRemote: async () => {
            throw new Error("Actor Turso sync handle did not open.");
          },
          pushToRemote: async () => {
            throw new Error("Actor Turso sync handle did not open.");
          },
        });
        return;
      }
      // Poll until the lazily-opened sync handle appears. `safeSetTimeout` guards the callback: a
      // synchronous throw in `waitForHandle` (e.g. `start(sync)` racing the post-sign-in in-place
      // connection reopen) would otherwise escape this detached timer into the New-Arch C++
      // scheduler and `std::terminate` the process (HAN-36) instead of surfacing a red-box.
      pollTimer = safeSetTimeout(waitForHandle, HANDLE_POLL_INTERVAL_MS, "actor-turso-sync-poll");
    };
    waitForHandle();

    return () => {
      disposed = true;
      if (pollTimer !== undefined) {
        clearTimeout(pollTimer);
      }
      cancelBackgroundPush?.();
      autoPushChangeListener?.remove();
      disconnectAutoSync?.();
      cancelBackgroundPull?.();
      appStateListener?.remove();
      unwatchRecoveryHold?.();
      stopSyncPushRegistration?.();
      if (catchUpTimer !== undefined) {
        clearInterval(catchUpTimer);
      }
      setActorRemoteSync(null);
    };
  }, [deviceAccount, db, recoveryRef, userId]);

  return actorRemoteSync;
}
